import * as React from "react";
import { rankItem } from "@tanstack/match-sorter-utils";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import CustomTablePagination from "../../master/TablePagination";
import ComponentLoader from "../../ui/pageLoader/ComponentLoader";

import { deleteIconDark, editPencilIcon } from "../../../assetData/icons";
import { FaSync } from "react-icons/fa";
import { RiImageEditFill } from "react-icons/ri";
import { errorNotify } from "../../../Services/Notification";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({
    itemRank,
  });
  return itemRank.passed;
};

function Table(props) {
  const {
    tableData,
    columns,
    actionConfig = [],
    loading,
    setSelectedItem,
    action,
    buttonConfig,
    syncClicked,
    renderActions,
    getSelectionValue = (row) => row.template_id,
    showSyncButtons = true,
  } = props;
  const [data, setData] = React.useState([]);

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const handleSyncData = buttonConfig?.handleSyncData;

  const customTable = useReactTable({
    data,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  React.useEffect(() => {
    if (tableData) setData(tableData);
  }, [tableData]);

  const selectedData = customTable
    .getSelectedRowModel()
    .rows.map((row) => getSelectionValue(row.original))
    .filter((value) => value !== undefined && value !== null)
    ?.sort((a, b) => b - a);

  const handleSync = async (type) => {
    if (selectedData?.length > 0) {
      const res = await handleSyncData(selectedData[0], type);
      if (res) {
        customTable.resetRowSelection();
      }
    } else {
      errorNotify("Please select at least one template to sync");
    }
  };

  console.log("syncClicked:::", syncClicked?.includes("Dev"), syncClicked);
  if (!data) return null;

  return (
    <div>
      <div className={`flex w-full items-center justify-between `}>
        <div className={`flex w-full items-center gap-4`}>
          {actionConfig.map((action, idx) => (
            <div key={action.id} className="flex w-full items-center gap-4">
              <button
                className={`flex w-full items-center justify-center gap-2 whitespace-nowrap rounded border-[1px] border-[#636363] px-6 py-1 text-center font-bold text-[#636363] max-sm:w-full ${
                  action.disable ? "opacity-[100%]" : "opacity-[55%]"
                }`}
                onClick={action.actionHandler}
                disabled={!action.disable}
              >
                <img src={action.image} alt="i4Invest" />
                {action.name}
              </button>
            </div>
          ))}
          {showSyncButtons && (
          <div className="flex w-full items-center gap-4">
            <div
              onClick={() => handleSync("Dev")}
              disabled={syncClicked?.includes("Dev") || !selectedData?.length}
              className={`${
                syncClicked?.includes("Dev") || !selectedData?.length
                  ? "cursor-not-allowed opacity-[50%]"
                  : ""
              } flex min-w-[150px] cursor-pointer items-center justify-center gap-2 rounded border-[1px] border-[#636363] px-2 py-1 font-bold text-[#636363] `}
            >
              <FaSync
                className={syncClicked?.includes("Dev") ? "animate-spin" : ""}
              />
              DEV ({selectedData?.length || 0})
            </div>
            <div
              onClick={() => handleSync("Test")}
              disabled={syncClicked?.includes("Test") || !selectedData?.length}
              className={`${
                syncClicked?.includes("Test") || !selectedData?.length
                  ? "cursor-not-allowed opacity-[50%]"
                  : ""
              } flex min-w-[150px] cursor-pointer items-center justify-center gap-2 rounded border-[1px] border-[#636363] px-2 py-1 font-bold text-[#636363] `}
            >
              <FaSync />
              TEST ({selectedData?.length || 0})
            </div>
            <div
              onClick={() => handleSync("Prod")}
              disabled={syncClicked?.includes("Prod") || !selectedData?.length}
              className={`${
                syncClicked?.includes("Prod") || !selectedData?.length
                  ? "cursor-not-allowed opacity-[50%]"
                  : ""
              } flex min-w-[150px] cursor-pointer items-center justify-center gap-2 rounded border-[1px] border-[#636363] px-2 py-1 font-bold text-[#636363] `}
            >
              <FaSync />
              PROD ({selectedData?.length || 0})
            </div>
          </div>
          )}
        </div>
        <div className="flex w-full items-center justify-between ">
          <div className="mb-4 flex w-full items-center gap-2 space-x-2 md:justify-end">
            <div className="flex w-fit items-center gap-2 space-x-2">
              <label className="text-[#868686]">Show Rows: </label>
              <select
                className="text-dropdown-text w-[100px] bg-gray-100 py-[0.4rem] text-[15px] text-[#868686] focus:outline-none"
                onChange={(e) => {
                  const value =
                    e.target.value === "All"
                      ? data.length
                      : Number(e.target.value);
                  setPagination((old) => ({
                    ...old,
                    pageSize: value,
                  }));
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="All">All</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading && <ComponentLoader />}
      <div className="scrollbar-custom max-xl:overflow-x-scroll">
        <table className="w-full">
          <thead className=" bg-pri-clr-blue">
            {customTable.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className={`border-1 whitespace-nowrap border-black px-1 py-2 text-start text-[12px] font-bold uppercase text-[#F6F6F6] ${
                      header.id === "id" ? "text-center" : ""
                    }`}
                    style={{
                      textAlign: header.id === "id" ? "center" : "left",
                      width: header.id === "name" ? "60%" : "fit",
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <thead className="h-2">
            <tr></tr>
          </thead>
          <tbody className="border-[1px] border-neutral-clr-300  pt-2">
            {customTable.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell, index) => (
                  <td
                    key={cell.id}
                    className={`relative h-[29px] whitespace-nowrap border-[1px] border-neutral-clr-300 px-2 text-[12px] font-medium text-[#636363]`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    {cell.id.split("_")[1] === "action" && (
                      renderActions ? renderActions(cell.row.original) : (
                        <div className="flex items-center justify-around gap-3">
                          <button
                            onClick={() =>
                              setSelectedItem({
                                data: cell?.row?.original,
                                type: "edit",
                              })
                            }
                            disabled={!action?.edit_perm}
                          >
                            <img
                              src={editPencilIcon}
                              className={`${
                                action?.edit_perm ? "" : "opacity-[55%]"
                              }`}
                              alt="i4invest"
                            />
                            <p></p>
                          </button>
                          <button
                            type="button"
                            title="Update template image"
                            aria-label="Update template image"
                            onClick={() =>
                              setSelectedItem({
                                data: cell?.row?.original,
                                type: "update-image",
                              })
                            }
                            disabled={!action?.edit_perm}
                          >
                            <RiImageEditFill
                              className={`text-xl text-[#636363] ${
                                action?.edit_perm ? "" : "opacity-[55%]"
                              }`}
                            />
                          </button>
                          <button
                            onClick={() =>
                              setSelectedItem({
                                data: {
                                  broker_template_id:
                                    cell?.row?.original?.broker_template_id,
                                },
                                type: "delete",
                              })
                            }
                            disabled={!action?.delete_perm}
                          >
                            <img
                              src={deleteIconDark}
                              className={`${
                                action?.delete_perm ? "" : "opacity-[55%]"
                              }`}
                              alt="i4invest"
                            />
                            <p></p>
                          </button>
                        </div>
                      )
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && <CustomTablePagination table={customTable} />}
    </div>
  );
}

export default Table;
